import { useState, useCallback, useEffect, useMemo } from 'react';
import { useCart } from '../../../graphql/components/CartProvider';

const useBundleSelection = (isStadium = false) => {
  const { cart } = useCart();

  const bundleSelectionsFromCart = useMemo(() => {
    if (!cart?.order?.items) {
      if (isStadium) {
        return { outboundTier: 3, inboundTier: 3 };
      }
      return { outboundTier: null, inboundTier: null };
    }

    const bundleItems = cart.order.items.filter(
      (item) => item.__typename === 'BundleOrderItem'
    );
    const flightItems = cart.order.items.filter(
      (item) => item.__typename === 'FlightOrderItem'
    );

    let outboundTier = null;
    let inboundTier = null;

    if (isStadium && bundleItems.length === 0) {
      outboundTier = 3;
      inboundTier = 3;
    } else {
      const flightLegMap = new Map();
      flightItems.forEach((flightItem, index) => {
        const flightId = flightItem.flight?.id;
        if (flightId) {
          const leg = index === 0 ? 'outbound' : 'inbound';
          flightLegMap.set(flightId, leg);
        }
      });

      bundleItems.forEach((bundleItem) => {
        const tier = bundleItem.bundle?.tier || null;
        const bundleFlightId = bundleItem.flightId || bundleItem.flight?.id;

        if (bundleFlightId && flightLegMap.has(bundleFlightId)) {
          const leg = flightLegMap.get(bundleFlightId);
          if (leg === 'outbound') {
            outboundTier = tier;
          } else if (leg === 'inbound') {
            inboundTier = tier;
          }
        } else {
          const bundleString = JSON.stringify(bundleItem).toLowerCase();
          if (bundleItem.leg === 'departing' || bundleItem.leg === 'outbound') {
            outboundTier = tier;
          } else if (
            bundleItem.leg === 'returning' ||
            bundleItem.leg === 'inbound'
          ) {
            inboundTier = tier;
          } else if (
            bundleString.includes('depart') ||
            bundleString.includes('outbound')
          ) {
            outboundTier = tier;
          } else if (
            bundleString.includes('return') ||
            bundleString.includes('inbound')
          ) {
            inboundTier = tier;
          } else {
            const bundleIndex = bundleItems.indexOf(bundleItem);
            if (bundleIndex === 0) {
              outboundTier = tier;
            } else if (bundleIndex === 1) {
              inboundTier = tier;
            }
          }
        }
      });
    }

    const selections = { outboundTier, inboundTier };
    return selections;
  }, [cart?.order?.items, isStadium]);

  const [selectedBundles, setSelectedBundles] = useState(
    bundleSelectionsFromCart
  );

   useEffect(() => {
    setSelectedBundles((prev) => {
      const hasSelectionChanged =
        prev.outboundTier !== bundleSelectionsFromCart.outboundTier ||
        prev.inboundTier !== bundleSelectionsFromCart.inboundTier;

      if (!hasSelectionChanged) {
        return prev;
      }

      return bundleSelectionsFromCart;
    });
  }, [bundleSelectionsFromCart.outboundTier, bundleSelectionsFromCart.inboundTier]);

  // dateKey is used to track bundle selections by specific dates,
  // allowing users to select different bundle tiers for different travel dates
  const handleBundleSelect = useCallback((bundleData) => {
    const { leg, tier, dateKey } = bundleData;
    const tierKey = leg === 'departing' ? 'outboundTier' : 'inboundTier';

    setSelectedBundles((prev) => {
      const newState = {
        ...prev,
        [tierKey]: tier,
      };

      if (dateKey) {
        const prevTierByDate = prev?.tierByDate || {};
        const prevLegTierByDate = prevTierByDate?.[leg] || {};

        const resetLegTierByDate = Object.keys(prevLegTierByDate).reduce(
          (acc, key) => {
            acc[key] = null;
            return acc;
          },
          {}
        );

        newState.tierByDate = {
          ...prevTierByDate,
          [leg]: {
            ...resetLegTierByDate,
            [dateKey]: tier,
          },
        };
      }

      return newState;
    });
  }, []);

  const getBundleForLeg = useCallback(
    (leg) => {
      const tierKey = leg === 'departing' ? 'outboundTier' : 'inboundTier';
      return selectedBundles[tierKey];
    },
    [selectedBundles]
  );

  const getBundleItemForLeg = useCallback(
    (leg) => {
      if (!cart?.order?.items) return null;

      const bundleItems = cart.order.items.filter(
        (item) => item.__typename === 'BundleOrderItem'
      );
      const flightItems = cart.order.items.filter(
        (item) => item.__typename === 'FlightOrderItem'
      );
      for (const bundleItem of bundleItems) {
        const bundleFlightId = bundleItem.flightId || bundleItem.flight?.id;

        if (bundleFlightId) {
          const flightItem = flightItems.find(
            (f) => f.flight?.id === bundleFlightId
          );
          if (flightItem) {
            const flightIndex = flightItems.indexOf(flightItem);
            const isOutbound = flightIndex === 0;

            if (
              (leg === 'departing' && isOutbound) ||
              (leg === 'returning' && !isOutbound)
            ) {
              return bundleItem;
            }
          }
        }
      }
      const targetIndex = leg === 'departing' ? 0 : 1;
      return bundleItems[targetIndex] || null;
    },
    [cart?.order?.items]
  );

  const isBundlePersistedForLeg = useCallback(
    (leg) => {
      const cartTier =
        bundleSelectionsFromCart[
          leg === 'departing' ? 'outboundTier' : 'inboundTier'
        ];
      const currentTier =
        selectedBundles[leg === 'departing' ? 'outboundTier' : 'inboundTier'];
      return cartTier === currentTier && cartTier !== null;
    },
    [bundleSelectionsFromCart, selectedBundles]
  );

  const clearBundleSelection = useCallback(() => {
    setSelectedBundles(bundleSelectionsFromCart);
  }, [bundleSelectionsFromCart]);

  return {
    selectedBundles,
    handleBundleSelect,
    getBundleForLeg,
    getBundleItemForLeg,
    isBundlePersistedForLeg,
    clearBundleSelection,
    cartBundleSelections: bundleSelectionsFromCart,
  };
};

export default useBundleSelection;
